// src/components/editors/AboutEditor.jsx
import React, { useMemo, useRef, useState } from "react";
import {
  Box, TextField, Typography, Button, IconButton, Grid, Paper,
  Alert, LinearProgress, MenuItem, Select, FormControl, InputLabel
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import { useAuthContext } from "../../context/AuthContext";
import { useS3Upload } from "../../hooks/useS3Upload";

const MAX_MB = 10;

export default function AboutEditor({ value, onChange }) {
  const { userData } = useAuthContext();
  const { uploadOne } = useS3Upload();

  const v =
    value || {
      format: "about.v1",
      hero: [],
      intro: "",
      highlights: [],
      units: [],
      matriz_index: 0,
      disclaimer: "",
    };

  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ phase: "idle", idx: -1, pct: 0 });

  const prefix = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/contents/sobre/${yyyy}/${mm}`;
  }, [userData?.id_workspace]);

  const up = (patch) => onChange({ ...v, ...patch });

  /* ===== HERO (galeria) ===== */
  const setHero = (i, patch) => {
    const a = [...(v.hero || [])];
    a[i] = { ...a[i], ...patch };
    up({ hero: a });
  };
  const addHero = () => up({ hero: [...(v.hero || []), { src: "", alt: "" }] });
  const delHero = (i) => up({ hero: (v.hero || []).filter((_, idx) => idx !== i) });

  const onUploadHero = async (i, file) => {
    if (!file) return;
    if (!/^image\//i.test(file.type)) return setError("Envie apenas imagens.");
    if (file.size > MAX_MB * 1024 * 1024) return setError(`Imagem maior que ${MAX_MB}MB.`);

    try {
      setError(null);
      setProgress({ phase: "hero", idx: i, pct: 10 });
      const { url } = await uploadOne(file, { prefix });
      setProgress({ phase: "hero", idx: i, pct: 90 });
      setHero(i, { src: url });
    } catch (e) {
      setError(e?.message || "Falha no upload da imagem do Hero.");
    } finally {
      setProgress({ phase: "idle", idx: -1, pct: 0 });
    }
  };

  /* ===== HIGHLIGHTS (selos) ===== */
  const setHl = (i, patch) => {
    const a = [...(v.highlights || [])];
    a[i] = { ...a[i], ...patch };
    up({ highlights: a });
  };
  const addHl = () => up({ highlights: [...(v.highlights || []), { title: "", desc: "" }] });
  const delHl = (i) => up({ highlights: (v.highlights || []).filter((_, idx) => idx !== i) });

  /* ===== UNITS ===== */
  const setUnit = (i, patch) => {
    const a = [...(v.units || [])];
    a[i] = { ...a[i], ...patch };
    up({ units: a });
  };
  const addUnit = () =>
    up({
      units: [
        ...(v.units || []),
        { slug: "", name: "", address: "", cityUF: "", phone: "", mapQuery: "", image: "" },
      ],
    });
  const delUnit = (i) => up({ units: (v.units || []).filter((_, idx) => idx !== i) });

  const onUploadUnit = async (i, file) => {
    if (!file) return;
    if (!/^image\//i.test(file.type)) return setError("Envie apenas imagens.");
    if (file.size > MAX_MB * 1024 * 1024) return setError(`Imagem maior que ${MAX_MB}MB.`);

    try {
      setError(null);
      setProgress({ phase: "unit", idx: i, pct: 10 });
      const { url } = await uploadOne(file, { prefix });
      setProgress({ phase: "unit", idx: i, pct: 90 });
      setUnit(i, { image: url });
    } catch (e) {
      setError(e?.message || "Falha no upload da imagem da unidade.");
    } finally {
      setProgress({ phase: "idle", idx: -1, pct: 0 });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Sobre (about.v1)
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {progress.phase !== "idle" && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="indeterminate" />
          <Typography variant="caption" color="text.secondary">
            {progress.phase === "hero"
              ? `Enviando imagem da galeria #${progress.idx + 1}`
              : `Enviando imagem da unidade #${progress.idx + 1}`}
          </Typography>
        </Box>
      )}

      {/* Hero */}
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Galeria (Hero)
        </Typography>
        {v.hero?.map((h, i) => (
          <Box
            key={i}
            sx={{ display: "grid", gridTemplateColumns: "2fr 3fr auto auto", gap: 1, mb: 1, alignItems: "center" }}
          >
            <TextField label="Imagem (URL)" value={h.src} onChange={(e) => setHero(i, { src: e.target.value })} />
            <TextField label="Alt" value={h.alt} onChange={(e) => setHero(i, { alt: e.target.value })} />
            <UploadButton label="Upload" onPick={(file) => onUploadHero(i, file)} />
            <IconButton onClick={() => delHero(i)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Box>
        ))}
        <Button startIcon={<AddRoundedIcon />} onClick={addHero}>
          Adicionar imagem
        </Button>
      </Box>

      {/* Intro */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2">Texto institucional</Typography>
        <TextField
          value={v.intro || ""}
          onChange={(e) => up({ intro: e.target.value })}
          placeholder="Markdown/HTML (renderizado no site)"
          fullWidth
          multiline
          minRows={6}
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Highlights */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Selos / destaques
        </Typography>
        {v.highlights?.map((b, i) => (
          <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.4fr 2.6fr auto", gap: 1, mb: 1 }}>
            <TextField label="Título" value={b.title} onChange={(e) => setHl(i, { title: e.target.value })} />
            <TextField label="Descrição" value={b.desc} onChange={(e) => setHl(i, { desc: e.target.value })} />
            <IconButton onClick={() => delHl(i)}>
              <DeleteOutlineRoundedIcon />
            </IconButton>
          </Box>
        ))}
        <Button startIcon={<AddRoundedIcon />} onClick={addHl}>
          Adicionar selo
        </Button>
      </Box>

      {/* Matriz (índice da unidade para o mapa) */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Matriz (qual unidade usar no mapa)
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Unidade</InputLabel>
          <Select
            label="Unidade"
            value={Number.isFinite(+v.matriz_index) ? v.matriz_index : 0}
            onChange={(e) => up({ matriz_index: Number(e.target.value) || 0 })}
          >
            {(v.units || []).map((u, i) => (
              <MenuItem key={i} value={i}>
                {u?.name || `Unidade #${i + 1}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Unidades */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Unidades
        </Typography>
        {v.units?.map((u, i) => (
          <Grid key={i} container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField label="Nome" value={u.name} onChange={(e) => setUnit(i, { name: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Slug" value={u.slug} onChange={(e) => setUnit(i, { slug: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 1 }}>
              <TextField
                label="Imagem (URL)"
                value={u.image || ""}
                onChange={(e) => setUnit(i, { image: e.target.value })}
                fullWidth
              />
              <UploadButton small onPick={(file) => onUploadUnit(i, file)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Endereço" value={u.address} onChange={(e) => setUnit(i, { address: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Cidade/UF" value={u.cityUF} onChange={(e) => setUnit(i, { cityUF: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Telefone (opcional)" value={u.phone || ""} onChange={(e) => setUnit(i, { phone: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Google Maps query (endereço ou URL)" value={u.mapQuery} onChange={(e) => setUnit(i, { mapQuery: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <IconButton onClick={() => delUnit(i)}>
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button startIcon={<AddRoundedIcon />} onClick={addUnit}>
          Adicionar unidade
        </Button>
      </Box>
    </Paper>
  );
}

function UploadButton({ label = "Upload", small, onPick }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = (e.target.files && e.target.files[0]) || null;
          if (f) onPick(f);
          if (ref.current) ref.current.value = "";
        }}
      />
      <Button
        size={small ? "small" : "medium"}
        startIcon={<CloudUploadRoundedIcon />}
        variant="outlined"
        onClick={() => ref.current?.click()}
      >
        {label}
      </Button>
    </>
  );
}
